import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, CircularProgress, Icon, Typography } from '@/ui';

import { LoadError, PageLoading } from '../PageState';
import { Toast, useToast } from '../Toast';
import { copyText } from '../clipboard';
import { dayLabel } from '../format';

import LayerSwitches from './LayerSwitches';
import { GrantPoller, useGrantWait } from './useGrantWait';

import type { AISettings, McpGrant } from '@/store/api/mcpMemberApi';
import type { ReactNode } from 'react';

import {
  useGetCoachingNotesQuery,
  useGetCoachingNotesSettingsQuery,
  useGetMcpConnectGrantsQuery,
  useRevokeMcpConnectGrantMutation,
  useUpdateCoachingNotesSettingsMutation,
} from '@/store/api/mcpMemberApi';

const AI_CHOICES = [
  { maker: 'Anthropic', name: 'Claude' },
  { maker: 'OpenAI', name: 'ChatGPT' },
  { maker: 'Any app that supports MCP', name: 'Another AI' },
] as const;
type AiChoice = (typeof AI_CHOICES)[number]['name'];
type Step = 1 | 2 | 3 | 4;

const CLAUDE_NEW_CHAT_URL = 'https://claude.ai/new';
const panelClass = 'rounded-3xl bg-white p-6 shadow';
const linkClass = 'text-sage-500';

const lowerToday = (label: string) => (label === 'Today' ? 'today' : label);

const ConnectAiPage = () => {
  const grantsQuery = useGetMcpConnectGrantsQuery();
  const settingsQuery = useGetCoachingNotesSettingsQuery();
  const notesQuery = useGetCoachingNotesQuery();
  const [updateSettings, { isLoading: savingLayers }] =
    useUpdateCoachingNotesSettingsMutation();
  const [revokeGrant, { isLoading: revoking }] =
    useRevokeMcpConnectGrantMutation();
  const [step, setStep] = useState<Step>(1);
  const [ai, setAi] = useState<AiChoice>('Claude');
  const [baseline, setBaseline] = useState<readonly string[] | null>(null);
  const wait = useGrantWait(baseline);
  const { close, show, toast } = useToast();

  if (grantsQuery.isLoading || settingsQuery.isLoading) return <PageLoading />;
  if (
    grantsQuery.isError ||
    settingsQuery.isError ||
    !grantsQuery.data ||
    !settingsQuery.data
  ) {
    return (
      <LoadError
        onRetry={() => {
          grantsQuery.refetch().catch(() => undefined);
          settingsQuery.refetch().catch(() => undefined);
        }}
      />
    );
  }

  const { connector_url: connectorUrl, grants } = grantsQuery.data;
  const { settings } = settingsQuery.data;
  // Each switch sends a full object built from the cached settings, so stay
  // locked until the invalidation refetch confirms the last change.
  const layersLocked = savingLayers || settingsQuery.isFetching;
  const coach = notesQuery.data?.coach_first_name ?? '';
  const sample = (notesQuery.data?.categories.realized ?? []).slice(0, 3);
  const aiLabel = ai === 'Another AI' ? 'your AI' : ai;

  const saveLayers = async (next: AISettings) => {
    try {
      await updateSettings(next).unwrap();
    } catch {
      show('Couldn’t save. Try again.'); // NEW COPY
    }
  };
  const onLayers = (next: AISettings) => {
    if (layersLocked) return;
    saveLayers(next).catch(() => undefined);
  };
  const copyLink = async () => {
    show(
      (await copyText(connectorUrl))
        ? 'Link copied'
        : 'Couldn’t copy. Select the link and copy it.',
    ); // fallback: NEW COPY
  };
  const disconnect = async (grant: McpGrant) => {
    try {
      await revokeGrant({ grant_id: grant.id }).unwrap();
    } catch {
      show('Couldn’t disconnect. Try again.'); // NEW COPY
      return;
    }
    setBaseline(null);
    setStep(1);
    show(
      `Disconnected. ${grant.client_name} can’t see your coaching notes anymore.`,
    );
  };

  const header = (lede: string) => (
    <div className="mb-6">
      <Typography component="h1" variant="h4">
        Connect your AI
      </Typography>
      <Typography className="mt-2 text-cement-400" variant="body1">
        {lede}
      </Typography>
    </div>
  );

  const [primary] = grants;
  if (primary && baseline === null) {
    return (
      <div>
        {header(
          'Your AI only uses your coaching when you’re already talking to it. Mento never reaches out on its own.',
        )}
        {grants.map((grant) => (
          <div
            key={grant.id}
            aria-label={grant.client_name}
            className="mb-3 flex items-center gap-3 rounded-2xl bg-white px-4 py-3.5 text-[15px] shadow"
            role="group"
          >
            <span
              aria-hidden="true"
              className="size-2 rounded-full bg-sage-500"
            />
            <div className="min-w-0 flex-1">
              <b>Connected to {grant.client_name}</b> · since{' '}
              {lowerToday(dayLabel(grant.created_at))}
              {/* NEW COPY: last-used line */}
              <div className="text-sm text-cement-300">
                {grant.last_used_at
                  ? `Last used ${lowerToday(dayLabel(grant.last_used_at))}`
                  : 'Not used yet'}
              </div>
            </div>
            <Button
              loading={revoking}
              size="small"
              variant="outlined"
              onClick={() => {
                disconnect(grant).catch(() => undefined);
              }}
            >
              Disconnect {grant.client_name}
            </Button>
          </div>
        ))}
        <div className={clsx(panelClass, 'mt-6')}>
          <Typography component="h2" variant="h6">
            What {primary.client_name} can see
          </Typography>
          <Typography className="mb-2 text-cement-400" variant="body2">
            Changes apply the next time your AI looks.
          </Typography>
          <LayerSwitches
            coachFirstName={coach}
            disabled={layersLocked}
            settings={settings}
            onChange={onLayers}
          />
        </div>
        <div className="mt-5">
          <Link className={linkClass} to="/m/settings/ai-moments">
            Choose when it comes up
          </Link>
        </div>
        <Toast toast={toast} onClose={close} />
      </div>
    );
  }

  let panel: ReactNode;
  if (step === 1) {
    panel = (
      <div className={panelClass}>
        <Typography component="h2" variant="h6">
          Your AI, with your coaching
        </Typography>
        <Typography className="mb-4 text-cement-400" variant="body1">
          When you draft a tricky message, prep a 1:1 or plan your week, your AI
          can check your coaching notes and shape its help around what you’re
          working on.
        </Typography>
        <ul className="m-0 mb-4 flex list-none flex-col gap-3 p-0">
          <li className="flex gap-3">
            <Icon>visibility</Icon>
            <span>
              Your AI sees your coaching notes: what you realized, your
              strengths, {coach || 'your coach'}’s guidance, your action items,
              and the people involved.
            </span>
          </li>
          <li className="flex gap-3">
            <Icon>person</Icon>
            <span>
              It only looks when you’re already talking to it, and only when the
              topic fits. Mento never reaches out.
            </span>
          </li>
          <li className="flex gap-3">
            <Icon>edit</Icon>
            <span>
              You decide what it sees, and when it brings things up. Change or
              disconnect anytime.
            </span>
          </li>
        </ul>
        <div className="mb-4 rounded-2xl bg-sunny-100 p-4">
          <small className="mb-2 block text-cement-300">
            From What You Realized, as your AI would read it
          </small>
          {sample.map((item) => (
            <p key={item.id} className="m-0 mb-2">
              {item.text}
            </p>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Link className={linkClass} to="/m/settings/coaching-notes">
            Review all your notes
          </Link>
          <Button variant="contained" onClick={() => setStep(2)}>
            Continue
          </Button>
        </div>
      </div>
    );
  } else if (step === 2) {
    panel = (
      <div className={panelClass}>
        <Typography component="h2" variant="h6">
          Choose what to share
        </Typography>
        <Typography className="mb-2 text-cement-400" variant="body1">
          Start with your notes. Add more if you want your AI to look closer.
        </Typography>
        <LayerSwitches
          coachFirstName={coach}
          disabled={layersLocked}
          settings={settings}
          onChange={onLayers}
        />
        <div className="mt-5 flex items-center justify-between">
          <Button variant="text" onClick={() => setStep(1)}>
            Back
          </Button>
          <Button variant="contained" onClick={() => setStep(3)}>
            Continue
          </Button>
        </div>
      </div>
    );
  } else if (step === 3) {
    panel = (
      <div className={panelClass}>
        <Typography component="h2" variant="h6">
          Add Mento to your AI
        </Typography>
        <Typography className="mb-3 text-cement-400" variant="body1">
          Which AI do you use?
        </Typography>
        <div className="mb-4 grid gap-2 sm:grid-cols-3">
          {AI_CHOICES.map((choice) => (
            <button
              key={choice.name}
              aria-pressed={ai === choice.name}
              className={clsx(
                'flex flex-col rounded-2xl border border-solid p-3 text-left',
                ai === choice.name
                  ? 'border-sage-500 bg-sage-200'
                  : 'border-cement-200 bg-white',
              )}
              type="button"
              onClick={() => setAi(choice.name)}
            >
              <b>{choice.name}</b>
              <span className="text-sm text-cement-400">{choice.maker}</span>
            </button>
          ))}
        </div>
        <div className="mb-3 flex items-center gap-3 rounded-2xl bg-sunny-100 p-3">
          <code className="min-w-0 flex-1 break-all">{connectorUrl}</code>
          <Button
            size="small"
            startIcon={<Icon>content_copy</Icon>}
            variant="contained"
            onClick={() => {
              copyLink().catch(() => undefined);
            }}
          >
            Copy link
          </Button>
        </div>
        <Typography className="mb-4 text-cement-400" variant="body2">
          In {aiLabel}, add a new connector and paste this link. You’ll be asked
          to sign in with Mento. The link alone doesn’t give anyone access.
        </Typography>
        <div className="flex items-center justify-between">
          <Button variant="text" onClick={() => setStep(2)}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setBaseline(grants.map((grant) => grant.id));
              setStep(4);
            }}
          >
            I’ve added it
          </Button>
        </div>
      </div>
    );
  } else if (wait.status === 'found' && wait.grant) {
    const name = wait.grant.client_name;
    panel = (
      <div className={panelClass}>
        <div className="mb-3 text-sage-500">
          <Icon>check</Icon>
        </div>
        <Typography component="h2" variant="h6">
          {name} is connected
        </Typography>
        <Typography className="mb-4 text-cement-400" variant="body1">
          Next time you ask {name} for help with your manager, a 1:1 or a busy
          week, it can check your coaching notes. You’ll see “Mento” when it
          does.
        </Typography>
        <div className="flex items-center justify-between">
          <Link className={linkClass} to="/m/settings/ai-moments">
            Choose when it comes up
          </Link>
          {ai === 'Claude' && (
            <a
              className="rounded-full bg-sage-500 px-4 py-2 font-bold text-white no-underline"
              href={CLAUDE_NEW_CHAT_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              Try it in Claude
            </a>
          )}
        </div>
      </div>
    );
  } else if (wait.status === 'timed-out') {
    panel = (
      <div className={panelClass}>
        {/* NEW COPY: timeout state */}
        <Typography component="h2" variant="h6">
          We haven’t heard from {aiLabel} yet.
        </Typography>
        <Typography className="mb-4 text-cement-400" variant="body1">
          Check that you finished signing in with Mento there.
        </Typography>
        <div className="flex items-center justify-between">
          <Button
            variant="text"
            onClick={() => {
              setBaseline(null);
              setStep(3);
            }}
          >
            Back
          </Button>
          <Button variant="contained" onClick={() => wait.restart()}>
            Check again
          </Button>
        </div>
      </div>
    );
  } else {
    panel = (
      <div className={panelClass}>
        {/* NEW COPY: waiting state */}
        <Typography component="h2" variant="h6">
          Waiting for {aiLabel}…
        </Typography>
        <Typography className="mb-4 text-cement-400" variant="body1">
          Finish signing in with Mento in {aiLabel}. This page updates when it’s
          connected.
        </Typography>
        <CircularProgress aria-label="Waiting" size={24} />
        <div className="mt-4">
          <Button
            variant="text"
            onClick={() => {
              setBaseline(null);
              setStep(3);
            }}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {wait.pollingActive && <GrantPoller />}
      {header(
        'Bring your coaching into Claude, ChatGPT or another AI you use at work.',
      )}
      <div
        aria-label={`Step ${step} of 4`}
        className="mb-5 flex gap-1.5"
        role="group"
      >
        {[1, 2, 3, 4].map((index) => (
          <span
            key={index}
            className={clsx(
              'h-1 flex-1 rounded-full',
              index <= step ? 'bg-sage-500' : 'bg-cement-200',
            )}
          />
        ))}
      </div>
      {panel}
      <Toast toast={toast} onClose={close} />
    </div>
  );
};

export default ConnectAiPage;
